import React, { useEffect, useState } from "react";
import { Button, InputNumber } from "antd";
import { ArrowRightOutlined, ReloadOutlined } from "@ant-design/icons";
import { generateClient } from "aws-amplify/api";
import { getTableID } from "../../Services/authServices";
import { userMarkupsByUserID } from "../../graphql/queries";
import canadaFlag from "../../Image/ca-circle.png";
import barbadosFlag from "../../Image/flag/barbados.png";

const API = generateClient();

const MAX_SOURCE_WHOLE_DIGITS = 9;
const MAX_CONVERTED_WHOLE_DIGITS = 10;

const sanitizeAmountInput = (raw, maxWholeDigits = MAX_SOURCE_WHOLE_DIGITS) => {
  if (!raw) return "";
  let cleaned = String(raw).replace(/,/g, "").replace(/[^\d.]/g, "");
  const firstDotIndex = cleaned.indexOf(".");
  if (firstDotIndex !== -1) {
    cleaned =
      cleaned.slice(0, firstDotIndex + 1) +
      cleaned.slice(firstDotIndex + 1).replace(/\./g, "");
  }
  const [wholePart = "", decimalPart = ""] = cleaned.split(".");
  const whole = wholePart.slice(0, maxWholeDigits);
  const decimal = decimalPart.slice(0, 2);
  return cleaned.includes(".") ? `${whole}.${decimal}` : whole;
};

const formatAmountInput = (
  value,
  info,
  maxWholeDigits = MAX_SOURCE_WHOLE_DIGITS,
) => {
  if (value === null || value === undefined || value === "") return "";
  const sanitized = sanitizeAmountInput(value, maxWholeDigits);
  if (!sanitized) return "";

  if (info?.userTyping) {
    const [whole, decimal] = sanitized.split(".");
    const wholeWithCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decimal !== undefined ? `${wholeWithCommas}.${decimal}` : wholeWithCommas;
  }

  const numeric = Number(sanitized);
  if (Number.isNaN(numeric)) return "";
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const handleAmountKeyDown = (
  event,
  maxWholeDigits = MAX_SOURCE_WHOLE_DIGITS,
) => {
  if (["e", "E", "+", "-"].includes(event.key)) {
    event.preventDefault();
    return;
  }

  const { key, target } = event;
  const inputValue = String(target?.value || "");
  const raw = inputValue.replace(/,/g, "");
  const dotIndex = raw.indexOf(".");
  const [whole = "", decimal = ""] = raw.split(".");

  if (key === ".") {
    if (dotIndex !== -1) event.preventDefault();
    return;
  }

  if (!/^\d$/.test(key)) return;

  const selectionStart = target?.selectionStart ?? inputValue.length;
  const selectionEnd = target?.selectionEnd ?? inputValue.length;
  if (selectionStart !== selectionEnd) return;

  const rawCursorPos = inputValue.slice(0, selectionStart).replace(/,/g, "").length;
  const isEditingDecimal = dotIndex !== -1 && rawCursorPos > dotIndex;

  if (!isEditingDecimal && whole.length >= maxWholeDigits) {
    event.preventDefault();
    return;
  }

  if (isEditingDecimal && decimal.length >= 2) {
    event.preventDefault();
  }
};

const ConverterCard = ({
  walletCurrency,
  onRefreshRate,
  onContinueSend,
}) => {
  const [sendAmount, setSendAmount] = useState(null);
  const [receiveAmount, setReceiveAmount] = useState(null);
  const [lastEditedField, setLastEditedField] = useState("send");
  const [sendToReceiveRate, setSendToReceiveRate] = useState(0);

  const normalizedWalletCurrency = String(walletCurrency || "CAD").toUpperCase();
  const receiveCurrency = normalizedWalletCurrency === "BBD" ? "CAD" : "BBD";
  const sendCurrencyFlag =
    normalizedWalletCurrency === "BBD" ? barbadosFlag : canadaFlag;
  const receiveCurrencyFlag =
    receiveCurrency === "BBD" ? barbadosFlag : canadaFlag;

  const fetchRate = async () => {
    try {
      const tableID = await getTableID();
      const adminQuery = `query MyQuery {
        listAdmins {
          items {
            finalCADToBBD
            finalBBDToCAD
          }
        }
      }`;
      const [adminResponse, markupResponse] = await Promise.all([
        API.graphql({ query: adminQuery }),
        tableID
          ? API.graphql({
              query: userMarkupsByUserID,
              variables: { userID: tableID },
            })
          : Promise.resolve(null),
      ]);

      const adminItem = adminResponse?.data?.listAdmins?.items?.[0];
      const finalCADToBBD = Number(adminItem?.finalCADToBBD || 0);
      const finalBBDToCAD = Number(adminItem?.finalBBDToCAD || 0);

      const markupItem = markupResponse?.data?.userMarkupsByUserID?.items?.[0];
      const markUpRateCAD = Number(markupItem?.markUpRateCAD || 0);
      const markUpRateBBD = Number(markupItem?.markUpRateBBD || 0);

      const cadToBBD = finalCADToBBD * (1 - markUpRateCAD / 100);
      const bbdToCAD = finalBBDToCAD * (1 - markUpRateBBD / 100);

      const nextRate =
        normalizedWalletCurrency === "BBD" ? bbdToCAD : cadToBBD;

      if (nextRate > 0) {
        setSendToReceiveRate(nextRate);
      }
    } catch (error) {
      console.error("Error fetching exchange rate:", error);
    }
  };

  useEffect(() => {
    fetchRate();
  }, [normalizedWalletCurrency]);

  useEffect(() => {
    if (!sendToReceiveRate) return;
    if (lastEditedField === "send" && sendAmount !== null && sendAmount !== undefined) {
      setReceiveAmount(Number((Number(sendAmount) * sendToReceiveRate).toFixed(2)));
    } else if (
      lastEditedField === "receive" &&
      receiveAmount !== null &&
      receiveAmount !== undefined &&
      sendToReceiveRate > 0
    ) {
      setSendAmount(Number((Number(receiveAmount) / sendToReceiveRate).toFixed(2)));
    }
  }, [sendToReceiveRate, sendAmount, receiveAmount, lastEditedField]);

  const handleSendChange = (value) => {
    setLastEditedField("send");
    setSendAmount(value);
    if (value === null || value === undefined || value === "") {
      setReceiveAmount(null);
      return;
    }
    setReceiveAmount(Number((Number(value) * sendToReceiveRate).toFixed(2)));
  };

  const handleReceiveChange = (value) => {
    setLastEditedField("receive");
    setReceiveAmount(value);
    if (value === null || value === undefined || value === "") {
      setSendAmount(null);
      return;
    }
    if (sendToReceiveRate > 0) {
      setSendAmount(Number((Number(value) / sendToReceiveRate).toFixed(2)));
    }
  };

  return (
    <div className="appTableCard dashboardCalcCard">
      <div className="dashboardConverterHeaderV2">
        <div className="dashboardConverterRatePill">
          1 {walletCurrency} = {Number(sendToReceiveRate || 0).toFixed(4)}{" "}
          {receiveCurrency}
        </div>
        <Button
          type="text"
          icon={<ReloadOutlined />}
          className="dashboardRateRefresh"
          onClick={() => {
            fetchRate();
            onRefreshRate?.();
          }}
        />
      </div>

      <div className="dashboardConverterAmountsRow">
        <div className="dashboardConverterAmountCard">
          <label className="dashboardAmountLabelV2">You send</label>
          <div className="dashboardConverterInputRowV2">
            <span className="dashboardConverterCurrencyV2">
              <img
                src={sendCurrencyFlag}
                alt={walletCurrency}
                className="dashboardCurrencyFlag"
              />
              <span className="dashboardConverterCurrencyText">{walletCurrency}</span>
            </span>
            <InputNumber
              className="dashboardAmountInput dashboardConverterInput"
              value={sendAmount}
              controls={false}
              min={0}
              precision={2}
              placeholder="0.00"
              onChange={handleSendChange}
              formatter={(value, info) =>
                formatAmountInput(value, info, MAX_SOURCE_WHOLE_DIGITS)
              }
              parser={(value) => sanitizeAmountInput(value, MAX_SOURCE_WHOLE_DIGITS)}
              onKeyDown={(event) =>
                handleAmountKeyDown(event, MAX_SOURCE_WHOLE_DIGITS)
              }
            />
          </div>
        </div>

        <div className="dashboardConverterArrowV2" aria-hidden="true">
          <ArrowRightOutlined />
        </div>

        <div className="dashboardConverterAmountCard">
          <label className="dashboardAmountLabelV2">Recipient gets</label>
          <div className="dashboardConverterInputRowV2">
            <span className="dashboardConverterCurrencyV2">
              <img
                src={receiveCurrencyFlag}
                alt={receiveCurrency}
                className="dashboardCurrencyFlag"
              />
              <span className="dashboardConverterCurrencyText">{receiveCurrency}</span>
            </span>
            <InputNumber
              className="dashboardAmountInput dashboardConverterInput"
              value={receiveAmount}
              controls={false}
              min={0}
              precision={2}
              placeholder="0.00"
              onChange={handleReceiveChange}
              formatter={(value, info) =>
                formatAmountInput(value, info, MAX_CONVERTED_WHOLE_DIGITS)
              }
              parser={(value) =>
                sanitizeAmountInput(value, MAX_CONVERTED_WHOLE_DIGITS)
              }
              onKeyDown={(event) =>
                handleAmountKeyDown(event, MAX_CONVERTED_WHOLE_DIGITS)
              }
            />
          </div>
        </div>
      </div>

      <div className="dashboardConverterBtnWrap">
        <Button
          className="signupbtn dashboardContinueBtn"
          type="primary"
          onClick={() =>
            onContinueSend({
              sendAmount,
              receiveAmount,
              receiveCurrency,
            })
          }
          block
        >
          Send Remittance
        </Button>
      </div>
    </div>
  );
};

export default ConverterCard;
